// Shared normalizer for operator-entered external URLs in the CMS link fields
// (area-discover POI board + property-location surroundings picker).
//
// The render-time gate (studio-sites `isSafeExternalUrl`) only accepts values
// that URL-parse to an http/https scheme, so a bare "example.com" or
// "www.example.com" silently renders as plain text instead of a link. We add the
// scheme on the CMS side, on blur, so what the operator types becomes a working
// link — and reflect the corrected value straight back into the field.
//
// Agreed rules:
//   * preserve the host verbatim — never strip "www."
//   * never touch an existing scheme — an "http://" stays "http://"
//   * only ever PREPEND "https://" — so this can never turn a value into a
//     javascript:/data: link (the render gate stays the final safety check)
//   * normalize only bare host-like values (a dot, no whitespace); anything else
//     is returned untouched.

/** True when the value already carries a URL scheme we must not touch. */
function hasScheme(s: string): boolean {
  // Authority-based schemes (http://, https://, ftp://, …). Note the required
  // "//" — this deliberately does NOT match "example.com:8080", whose ":" is a
  // port, so a host:port still gets an https:// prefix below.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return true;
  // Common schemeless-authority schemes a link field might legitimately carry.
  return /^(mailto|tel|sms):/i.test(s);
}

/** Add "https://" to a scheme-less host-like value; otherwise return as-is. */
export function normalizeExternalUrl(raw: string): string {
  const s = raw.trim();
  if (!s) return "";
  if (hasScheme(s)) return s;
  // Require a dot and no whitespace so we don't prepend to obvious non-URLs
  // ("call the office", "n/a", a single word).
  if (/\s/.test(s) || !s.includes(".")) return s;
  return `https://${s}`;
}

/**
 * Safe http(s) href for a preview affordance, or null when the value isn't a
 * usable external link. Mirrors studio-sites `isSafeExternalUrl` so the CMS
 * preview link matches exactly what the rendered site will (or won't) link.
 */
export function externalHref(raw: string): string | null {
  const normalized = normalizeExternalUrl(raw);
  if (!normalized) return null;
  try {
    const u = new URL(normalized);
    return u.protocol === "http:" || u.protocol === "https:" ? normalized : null;
  } catch {
    return null;
  }
}
